import type { OrderResponse, OrderService } from '@/types/property.types';
import { formatDate } from '@/util/formatting';

/** Everything the UTC badge reads from the order itself. */
export type UtcDetailSource = Pick<
  OrderResponse,
  | 'unableToComplete'
  | 'unableToCompleteReason'
  | 'unableToCompleteNotes'
  | 'orderDate'
>;

/** The UTC badge's detail card content. */
export interface UtcDetailModel {
  reason: string | null;
  /** The PlanOtech's note. */
  notes: string | null;
  /** Originally-ordered services, list-formatted, or null without a snapshot. */
  originalServicesText: string | null;
  /** Order date as "Jun 3, 2026". */
  submittedDateText: string | null;
}

/** 0 → "-", 1 → "A", 2 → "A & B", 3+ → "A, B & C". Blank names dropped. */
export function formatServiceList(names: string[]): string {
  const list = names.filter(Boolean);
  if (list.length === 0) return '-';
  if (list.length === 1) return list[0];
  return `${list.slice(0, -1).join(', ')} & ${list[list.length - 1]}`;
}

/** The badge's model, or null when the order is not marked Unable to
    Complete. Driven ONLY by the flag, never by the order's services.
    `originalServices` is the Services Ordered model's `originalServices`, so
    the card's "Original Order" and the struck-through pills share one rule. */
export function buildUtcDetailModel(
  source: UtcDetailSource,
  originalServices: OrderService[] | null
): UtcDetailModel | null {
  if (source.unableToComplete !== true) return null;

  return {
    reason: source.unableToCompleteReason ?? null,
    notes: source.unableToCompleteNotes ?? null,
    originalServicesText: originalServices
      ? formatServiceList(originalServices.map((s) => s.name))
      : null,
    submittedDateText: source.orderDate ? formatDate(source.orderDate) : null
  };
}
